import { useMsal } from '@azure/msal-react';
import * as React from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import pkg from '../../../package.json';
import useUserAuth from '../../hooks/useUserAuth';
import usePageTitle from '../../hooks/usePageTitle';
import { useTheme } from '../../contexts/ThemeContext';
import ChangelogDialog from '../ChangelogDialog';
import UserProfileDialog from '../UserProfileDialog';

// mui imports
import Apartment from '@mui/icons-material/Apartment';
import Brightness4 from '@mui/icons-material/Brightness4';
import Brightness7 from '@mui/icons-material/Brightness7';
import BusinessCenterIcon from '@mui/icons-material/BusinessCenter';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';
import MenuIcon from '@mui/icons-material/Menu';
import Person from '@mui/icons-material/Person';
import Settings from '@mui/icons-material/Settings';
import TimelineIcon from '@mui/icons-material/Timeline';
import AppBar from '@mui/material/AppBar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CssBaseline from '@mui/material/CssBaseline';
import Divider from '@mui/material/Divider';
import Drawer from '@mui/material/Drawer';
import IconButton from '@mui/material/IconButton';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemIcon from '@mui/material/ListItemIcon';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { InlineDevConsole, initConsoleCapture } from '../fetch-dev-console/src';

const iconProps = {
  sx: {
    color: '#C9B06E',
    /*     fill: `url(#linearColors)`,
     */
  },
};

import { pipelineNavLink } from './pipelineNavLink.jsx';

const STATIC_PAGE_TITLES = new Map([
  ['/associations', 'Associations'],
  ['/pipeline', 'Pipeline'],
  ['/deal-setup', 'Deal Setup'],
  ['/reporting', 'Reporting'],
  ['/reporting/sales-dashboard', 'Sales Dashboard'],
  ['/reporting/lead-buying-insights', 'Lead Buying Insights'],
  ['/action-items', 'Action Items'],
  ['/action-items-mockup', 'Action Items Mockup'],
  ['/test-nav', 'Navigation Test'],
  ['/download', 'Buildium Data'],
  ['/admin', 'Admin'],
  ['/admin/file-summarizer', 'Admin · File Summarizer'],
  ['/admin/user-registry', 'Admin · User Registry'],
  ['/admin/raw-records', 'Admin · Raw Records'],
  ['/admin/trash', 'Admin · Trash Dashboard'],
  ['/admin/records', 'Admin · Raw Records'],
  ['/admin/field-manager', 'Admin · Field Manager'],
  ['/admin/company-profile', 'Admin · Company Profile'],
  ['/admin/templates', 'Admin · Templates'],
  ['/admin/email-templates', 'Admin · Email Templates'],
  ['/admin/name-verification', 'Admin · Name Verification'],
  ['/admin/association-cleanup', 'Admin · Association Cleanup'],
  ['/associations/duplicates', 'Associations · Duplicates'],
  ['/associations/progress-bar', 'Associations · Progress'],
  ['/associations/form', 'Associations · Form'],
]);

const ASSOCIATION_DETAIL_REGEX = /^\/associations\/[^/]+$/;

const resolvePageTitle = pathname => {
  if (STATIC_PAGE_TITLES.has(pathname)) {
    return STATIC_PAGE_TITLES.get(pathname);
  }
  if (ASSOCIATION_DETAIL_REGEX.test(pathname)) {
    return 'Association';
  }
  if (pathname.startsWith('/admin/')) {
    return 'Admin';
  }
  return null;
};
export const navLinks = [
  {
    title: 'Associations',
    icon: <Apartment {...iconProps} />,
    href: 'associations',
  },
  pipelineNavLink,
  {
    title: 'Deal Setup',
    icon: <BusinessCenterIcon {...iconProps} />,
    href: 'deal-setup',
  },
  {
    title: 'Dashboard',
    icon: <DashboardIcon {...iconProps} />,
    href: 'reporting/sales-dashboard',
  },
  {
    title: 'Lead Buying',
    icon: <InsightsIcon {...iconProps} />,
    href: 'reporting/lead-buying-insights',
  },
  {
    title: 'Action Items',
    icon: <TimelineIcon {...iconProps} />,
    href: 'action-items',
  },
];

export const adminNavLinks = [
  {
    title: 'Associations',
    icon: <Apartment {...iconProps} />,
    href: 'associations',
  },
  pipelineNavLink,
  {
    title: 'Deal Setup',
    icon: <BusinessCenterIcon {...iconProps} />,
    href: 'deal-setup',
  },
  {
    title: 'Dashboard',
    icon: <DashboardIcon {...iconProps} />,
    href: 'reporting/sales-dashboard',
  },
  {
    title: 'Lead Buying',
    icon: <InsightsIcon {...iconProps} />,
    href: 'reporting/lead-buying-insights',
  },
  {
    title: 'Action Items',
    icon: <TimelineIcon {...iconProps} />,
    href: 'action-items',
  },
];

export const logout = async instance => instance.logoutRedirect();

export default function PageLayout({ children }) {
  // User authentication
  const { isAdmin } = useUserAuth();

  // Theme management
  const { mode, toggleTheme } = useTheme();

  const [mobileOpen, setMobileOpen] = React.useState(false);
  const [isClosing, setIsClosing] = React.useState(false);
  const [changelogOpen, setChangelogOpen] = React.useState(false);
  const [userProfileOpen, setUserProfileOpen] = React.useState(false);

  // Initialize console capture for FetchDevConsole
  React.useEffect(() => {
    try {
      initConsoleCapture({
        maxMessages: 100,
        captureNetworkErrors: true,
        captureConsoleErrors: true,
      });
      console.log('FetchDevConsole initialized successfully');
    } catch (error) {
      console.warn('Failed to initialize FetchDevConsole:', error);
    }
  }, []);
  const navigate = useNavigate();
  const { instance } = useMsal();
  const location = useLocation();
  const defaultPageTitle = resolvePageTitle(location.pathname);
  usePageTitle({ page: defaultPageTitle });
  /*   const params = useParams();
   */
  React.useEffect(() => {
    const pathname = location.pathname;

    // Save the current page to localStorage for persistence on refresh
    // BUT exclude specific association URLs to prevent redirect loops on errors
    if (!pathname.match(/^\/associations\/[a-f0-9-]{36}$/i)) {
      localStorage.setItem('lastVisitedPage', pathname);
    }

    if (!pathname.includes('vendors')) {
      localStorage.removeItem('vendorsStoredQuickValues');
    }
    if (!pathname.includes('sites')) {
      localStorage.removeItem('storedQuickFilterValues');
    }
    if (!pathname.includes('clients')) {
      localStorage.removeItem('clientStoredQuickValues');
    }
  }, [location]);

  const linksToRender = isAdmin ? adminNavLinks : navLinks;

  const handleDrawerClose = () => {
    setIsClosing(true);
    setMobileOpen(false);
  };

  const handleDrawerTransitionEnd = () => {
    setIsClosing(false);
  };

  const handleDrawerToggle = () => {
    if (!isClosing) {
      setMobileOpen(!mobileOpen);
    }
  };

  const handleNavClick = href => {
    navigate(`/${href}`);
    handleDrawerClose();
  };

  const drawer = (
    <div
      style={{
        backgroundColor: '#091722',
        height: '100vh',
        position: 'fixed',
        top: 0,
        width: 80,
        color: 'white',
      }}
    >
      <Toolbar />
      <Divider />
      <List>
        {linksToRender.map(link => (
          <ListItem
            key={link.title}
            disablePadding
            sx={{
              bgcolor: '#091722',
            }}
          >
            <ListItemButton
              name={link.href}
              onClick={() => handleNavClick(link.href)}
              data-testid={`${link.title}-button`}
              sx={{
                minHeight: 48,
                justifyContent: 'center',
                px: 2.5,
              }}
              title={link.title}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  justifyContent: 'center',
                }}
              >
                {link.icon}
              </ListItemIcon>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
      <Divider />
    </div>
  );

  return (
    <>
      <svg width={0} height={0}>
        <linearGradient id='linearColors' x1={1} y1={0} x2={1} y2={1}>
          <stop offset={0} stopColor='#EDFD1D' />
          <stop offset={1} stopColor='#039A77' />
        </linearGradient>
      </svg>
      <Box data-testid='page-layout' sx={{ display: 'flex' }}>
        <CssBaseline />
        <AppBar
          position='fixed'
          sx={{
            bgcolor: '#C9B06E',
            color: 'white',
            width: { md: `calc(100% - 80px)` },
            ml: { md: `80px` },
          }}
        >
          <Toolbar variant='dense'>
            <IconButton
              color='inherit'
              aria-label='open drawer'
              edge='start'
              onClick={handleDrawerToggle}
              sx={{ mr: 2, display: { sm: 'block', md: 'none' } }}
            >
              <MenuIcon />
            </IconButton>

            <Typography
              variant='h6'
              sx={{ display: 'flex', flexGrow: 1, ml: 1, cursor: 'pointer', alignItems: 'center' }}
              noWrap
              component='div'
              title={`ShieldCRM v${pkg.version}`}
              onClick={() => setChangelogOpen(true)}
            >
              ShieldCRM
              {(import.meta.env.DEV ||
                window.location.hostname === 'localhost' ||
                window.location.hostname === '127.0.0.1') && (
                <Box
                  component='span'
                  sx={{
                    ml: 1,
                    mr: 1,
                    px: 1,
                    py: 0.25,
                    bgcolor: 'rgba(255,255,255,0.2)',
                    borderRadius: 1,
                    fontSize: '0.75rem',
                    fontWeight: 700,
                    border: '1px solid rgba(255,255,255,0.5)',
                    color: 'white',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                  }}
                >
                  Dev Mode
                </Box>
              )}
              v
              <span style={{ textDecoration: 'underline dotted', cursor: 'help' }}>
                {' '}
                {pkg.version.split('.').slice(0, 2).join('.')}{' '}
              </span>
            </Typography>

            {/* Console Debug Tool - positioned left of admin cog */}
            <InlineDevConsole size='small' color='inherit' />

            {/* User Profile Button */}
            <IconButton
              color='inherit'
              sx={{ mr: 2 }}
              onClick={() => setUserProfileOpen(true)}
              title='User Profile'
            >
              <Person />
            </IconButton>

            {/* Only show admin cog to admin users */}
            {isAdmin && (
              <IconButton
                color='inherit'
                sx={{ mr: 2 }}
                onClick={() => navigate('/admin')}
                title='Admin'
              >
                <Settings />
              </IconButton>
            )}

            {/* Theme toggle */}
            <IconButton
              color='inherit'
              sx={{ mr: 2 }}
              onClick={toggleTheme}
              title={mode === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
            >
              {mode === 'dark' ? <Brightness7 /> : <Brightness4 />}
            </IconButton>

            <Button sx={{ fontWeight: 'bold', color: 'white' }} onClick={() => logout(instance)}>
              Log out
            </Button>
          </Toolbar>
        </AppBar>

        <Box
          component='nav'
          sx={{ width: { md: 80 }, flexShrink: { md: 0 } }}
          aria-label='navigation menu'
        >
          {/* The implementation can be swapped with js to avoid SEO duplication of links. */}
          <Drawer
            variant='temporary'
            open={mobileOpen}
            onTransitionEnd={handleDrawerTransitionEnd}
            onClose={handleDrawerClose}
            ModalProps={{
              keepMounted: true, // Better open performance on mobile.
            }}
            sx={{
              display: { xs: 'block', md: 'none' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 80 },
            }}
          >
            {drawer}
          </Drawer>
          <Drawer
            variant='permanent'
            sx={{
              display: { xs: 'none', md: 'block' },
              '& .MuiDrawer-paper': { boxSizing: 'border-box', width: 80 },
            }}
            open
          >
            {drawer}
          </Drawer>
        </Box>

        <Box
          component='main'
          sx={{
            flexGrow: 1,
            p: 3,
            width: { md: `calc(100% - 80px)` },
          }}
        >
          <Toolbar variant='dense' />
          {children}
        </Box>

        {/* Changelog Dialog */}
        <ChangelogDialog open={changelogOpen} onClose={() => setChangelogOpen(false)} />

        {/* User Profile Dialog */}
        <UserProfileDialog open={userProfileOpen} onClose={() => setUserProfileOpen(false)} />
      </Box>
    </>
  );
}
